import { SimulationSteps } from 'features/simulation/types'
import { AllProducts } from 'types/product-data'
import { Language } from 'types/language'
import { store } from 'store'
import { CustomerFlows } from 'store/customer/enums'
import { normalizeText } from 'utils/format'
import { routes } from 'utils/routes'
import Router from 'next/router'
import { goToStep, toggleContactMe } from 'store/customer/slice'
import { EnvironmentValue } from 'types/common'
import { IS_STAGING } from 'constants/envs'
import { CUSTOMER_TYPES, ELProduct, NGProduct, Product } from '@boltenergy-be/shared'

/**
 * Convertes a file to base64
 *
 * @param {File} file
 * @returns {Promise<string|ArrayBuffer>}
 */
export const fileToBase64 = (file: File): Promise<string | ArrayBuffer> =>
  new Promise((resolve) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => {
      let result = reader.result as string
      if (result.startsWith('data:application/octet-stream')) {
        const ext = file.name.split('.').pop()?.toLowerCase()
        if (ext === 'heic') result = result.replace('application/octet-stream', 'image/heic')
        else if (ext === 'heif') result = result.replace('application/octet-stream', 'image/heif')
      }
      resolve(result)
    }
  })

/**
 * Generates an id to correlate a client-side log with the server-side log of the same request.
 *
 * @returns {string}
 */
export const generateRequestId = (): string => {
  // randomUUID needs a secure context, which a local http dev server isn't
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : Math.random().toString(36).slice(2)
}

/**
 * Returns the location.origin of the Bolt website
 * for each env.
 *
 * @returns string
 */
export const getBoltWebsiteOrigin = () => {
  switch (process.env.NEXT_PUBLIC_VERCEL_ENV ?? process.env.NODE_ENV) {
    // Local
    case 'development':
      return 'http://localhost:9000'

    // Staging
    case 'preview':
      return 'https://staging.boltenergie.be'

    // Production
    default:
      return 'https://boltenergie.be'
  }
}

/**
 * Returns the environment value based on the current environment
 *
 * @template T
 * @param {EnvironmentValue<T>} environmentValues
 * @returns {T}
 */
export const getEnvironmentValue = <T = unknown>(environmentValues: EnvironmentValue<T>): T => {
  return environmentValues[IS_STAGING ? 'STAGING' : 'PRODUCTION']
}

/**
 * Returns the pricelist for a given electricity product
 *
 * @param {ELProduct} product
 * @param {AllProducts} allProducts
 * @param {boolean} isCompany
 * @param {Language} language
 * @returns {string}
 */
export const getPricelistFromElectricityProducts = (
  product: ELProduct,
  allProducts: AllProducts,
  isCompany: boolean,
  language: Language
): string => {
  const pricelistType = isCompany ? CUSTOMER_TYPES.professional : CUSTOMER_TYPES.residential
  return allProducts ? allProducts.electricity[product].pricelists[pricelistType][language] : ''
}

/**
 * Returns the pricelist for a given gas product
 *
 * @param {NGProduct} product
 * @param {AllProducts} allProducts
 * @param {boolean} isCompany
 * @param {Language} language
 * @returns {string}
 */
export const getPricelistFromGasProducts = (
  product: NGProduct,
  allProducts: AllProducts,
  isCompany: boolean,
  language: Language
): string => {
  const pricelistType = isCompany ? CUSTOMER_TYPES.professional : CUSTOMER_TYPES.residential
  return allProducts ? allProducts.gas[product].pricelists[pricelistType][language] : ''
}

/**
 * Handles the contact me button click (for noOptinOne flow)
 * @param {Product} product
 * @param {() => void} onRequestClose
 */
export const handleContactMeClick = async (product: Product, onRequestClose?: () => void) => {
  const route = Router.route
  const isSimulationPage = route === '/simulation'

  store.dispatch(toggleContactMe(product))

  onRequestClose ? onRequestClose() : store.dispatch(goToStep({ flow: CustomerFlows.SIMULATION, step: SimulationSteps.PROPOSITION }))

  if (!isSimulationPage) return Router.push(routes(Router.locale).simulation)
}

/**
 * Scrolls to top of window
 */
export const handleScrollToTop = (): void => {
  window?.scrollTo({ behavior: 'smooth', top: 0 })
}

export const hasNoNumber = (text: string): boolean => {
  return !/\d/.test(text)
}

/**
 * Returns if the given barcode string is a valid EAN number
 *
 * @param {string} barcode
 * @returns {boolean}
 */
export const isValidEAN = (barcode: string): boolean => {
  // Check if EAN is 18 characters long
  if (barcode.length !== 18) return false

  // Check if the EAN starts with 54
  if (!barcode.startsWith('54')) return false

  // Check if EAN is a number
  if (isNaN(parseInt(barcode, 10))) return false

  // Check if the last digit is a number
  const lastDigit = parseInt(barcode.substring(barcode.length - 1), 10)
  if (isNaN(lastDigit)) return false

  // Reverse the all the numbers except the last
  const arr = barcode
    .substring(0, barcode.length - 1)
    .split('')
    .reverse()

  // Calculate the sum of all the numbers
  // Numbers on even positions are multiplied by 3 and then added
  // Numbers on odd positions are added without being multiplied
  let sum = 0
  for (let i = 0; i < arr.length; i++) {
    if (i % 2 === 0) {
      sum += parseInt(arr[i], 10) * 3
    } else {
      sum += parseInt(arr[i], 10)
    }
  }

  // Calculate the checksum
  let checkSum = sum % 10

  // Check & return if the checksum equals the last digit if the checksum equals 0
  if (checkSum === 0) return checkSum === lastDigit

  // Recalculate the checksum & check if last digit matches if checksum didn't equal 0
  checkSum = 10 - (sum % 10)

  return checkSum === lastDigit
}

/**
 * Returns if a given postal code is valid
 *
 * @param postalCode
 * @returns boolean
 */
export const isValidPostalCode = (postalCode: number): boolean => {
  return postalCode.toString().length === 4
}

/**
 * Removes spaces and dots in the given string & returns it
 *
 * @param {string} text
 * @returns {string}
 */
export const removeSpacesAndDots = (text: string): string => {
  // \s+ matches all whitespace characters, including spaces, non-break spaces, tabs, new lines and carriage returns
  const noSpaces = text.replace(/\s+/g, '')
  return noSpaces.replace(/\./g, '')
}

/**
 * Removes all non-alphanumeric characters in the given string & returns it
 *
 * @param {string} text
 * @returns {string}
 */
export const removeNonAlphanumericals = (text: string): string => {
  return text.replace(/[^a-zA-Z0-9]/g, '')
}

/**
 * Removes all non-numeric characters in the given string & returns it
 *
 * @param {string} text
 * @returns {string}
 */
export const removeNonNumbers = (text: string): string => {
  return text.replace(/[^0-9]/g, '')
}

/**
 * Shuffles any given array with the Fisher-Yates algorithm
 *
 * @param array
 * @returns any[]
 */
export const shuffleArray = (array: any[]): any[] => {
  const shuffled = [...array]

  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    const temp = shuffled[i]

    shuffled[i] = shuffled[j]
    shuffled[j] = temp
  }

  return shuffled
}

/**
 * Checks for matching strings in given keys
 * @param {string[]} keys
 * @param {string} query
 * @returns {boolean}
 */
export const searchMultipleKeys = (keys: string[], query: string) => keys.some((key) => normalizeText(key).includes(normalizeText(query)))

/**
 * Shallow compare two objects and return the keys that are different as an array. Optional normalize string values.
 * @param {Record<string, unknown>} obj1
 * @param {Record<string, unknown>} obj2
 * @param {{ normalize?: boolean, returnNonOverlappingKeys?: boolean }} options
 * @returns {string[]}
 */
export const shallowCompareObjects = <KeysToReturn extends string>(
  obj1: Record<string, unknown>,
  obj2: Record<string, unknown>,
  options?: {
    normalize?: boolean
    ignoreNonOverlappingKeys?: true | 'obj1' | 'obj2'
  }
): KeysToReturn[] => {
  const keysWithDifferentValue: KeysToReturn[] = []

  const remainingKeys = { ...obj2 }

  for (const key in obj1) {
    // Delete the key that has been compared
    delete remainingKeys[key]

    // If key is not present on second object, don't compare if returnMissingKeys option is not active
    if (!obj2[key] && (options?.ignoreNonOverlappingKeys === true || options?.ignoreNonOverlappingKeys === 'obj2')) continue

    // Determine which values to compare
    const obj1Value = typeof obj1[key] === 'string' && options?.normalize ? normalizeText(obj1[key]) : obj1[key]
    const obj2Value = typeof obj2[key] === 'string' && options?.normalize ? normalizeText(obj2[key]) : obj2[key]

    // Compare values &  optionally add to return value
    if (obj1Value !== obj2Value) {
      keysWithDifferentValue.push(key as KeysToReturn)
    }
  }

  return options?.ignoreNonOverlappingKeys === true || options?.ignoreNonOverlappingKeys === 'obj1'
    ? keysWithDifferentValue
    : ([...keysWithDifferentValue, ...Object.keys(remainingKeys)] as KeysToReturn[])
}
